import { Check, CheckCheck } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { ChatReadReceipt, TeamChatMessage } from '../../lib/teamChatApi';
import { Popover } from '../tasks/linear/Popover';

/**
 * WhatsApp-style delivery ticks on your own messages, driven entirely by the
 * read watermarks (`ChatConversationMember.lastReadAt`) that already produce
 * the unread badges — so the two can never contradict each other.
 *
 *   one grey tick     nobody has read it yet
 *   two grey ticks    some of the group has (never shown in a DM)
 *   two blue ticks    everyone expected to read it has
 */
export function SeenTicks({
  message,
  receipts,
  isGroup,
}: {
  message: TeamChatMessage;
  receipts: ChatReadReceipt[] | undefined;
  isGroup: boolean;
}) {
  const { t, locale } = useI18n();
  // Still loading: render nothing rather than a grey tick that may flip
  // immediately.
  if (!receipts) return null;

  const sentAt = Date.parse(message.createdAt);

  // Who the ticks are actually waiting on. Each exclusion below is a case that
  // would otherwise strand them grey forever.
  const expected = receipts.filter(
    (r) =>
      // You don't count as a reader of your own message.
      r.userId !== message.author.id &&
      // Deactivating an account leaves its membership row in place, and that
      // account will never read anything again.
      r.isActive !== false &&
      // Someone who joined after the message was sent has no watermark for it
      // and never will — expecting them would grey the whole back history.
      Date.parse(r.joinedAt) <= sentAt,
  );

  const seenBy = expected.filter(
    (r) => r.lastReadAt !== null && Date.parse(r.lastReadAt) >= sentAt,
  );

  // Nobody to wait on — a group of one, or a DM with a deactivated colleague.
  // One grey tick, which is honest: it was sent, it will never be read.
  if (expected.length === 0 || seenBy.length === 0) {
    return (
      <span title={t('teamChat.sent')} className="text-muted-foreground/50">
        <Check size={14} aria-label={t('teamChat.sent')} />
      </span>
    );
  }

  const all = seenBy.length === expected.length;
  const icon = (
    <CheckCheck size={14} className={all ? 'text-sky-500' : 'text-muted-foreground/50'} />
  );

  // A DM has exactly one reader, so there is nothing to list: the time is the
  // only extra information, and it fits in a tooltip.
  if (!isGroup) {
    const at = seenBy[0].lastReadAt;
    const label = `${t('teamChat.seen')}${
      at
        ? ` · ${new Date(at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`
        : ''
    }`;
    return (
      <span title={label} aria-label={label}>
        {icon}
      </span>
    );
  }

  const pending = expected.filter((r) => !seenBy.includes(r));

  return (
    <Popover
      align="end"
      width={220}
      trigger={({ open, toggle, ref }) => (
        <button
          type="button"
          ref={ref}
          onClick={toggle}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={t('teamChat.seenBy')}
          title={t('teamChat.seenBy')}
          className="rounded p-0.5 hover:bg-accent"
        >
          {icon}
        </button>
      )}
    >
      {() => (
        <div className="max-h-72 overflow-y-auto p-2 text-sm">
          <p className="px-1 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('teamChat.seenBy')}
          </p>
          <ul>
            {seenBy.map((r) => (
              <li key={r.userId} className="flex items-center gap-1.5 px-1 py-0.5 text-foreground">
                <CheckCheck size={13} className="shrink-0 text-sky-500" />
                <span className="truncate">{r.name}</span>
              </li>
            ))}
          </ul>
          {pending.length > 0 && (
            <>
              <p className="px-1 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('teamChat.notSeenYet')}
              </p>
              <ul>
                {pending.map((r) => (
                  <li key={r.userId} className="truncate px-1 py-0.5 text-muted-foreground">
                    {r.name}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </Popover>
  );
}
